/**
 * Central registry for every Polyglot instance in the app.
 *
 * Why this exists — bundle splitting.
 *
 * The app has ~20 Polyglot instances (apps/ui, libs/common-utils, libs/common/*,
 * libs/alerts, libs/asm, …). Without this registry each lib would have to import
 * its `en/es/fil` bundles eagerly, dragging every locale into the main JS bundle
 * (~2 MB extra). Instead, each lib calls `polyglotRegistry.register(instance,
 * { en, loaders: { es: () => import('./locales/es'), fil: () => import('./locales/fil') } })`.
 * The `en` baseline ships in the lib's main chunk; `es`/`fil` are returned as
 * dynamic `import()` callbacks, so webpack emits each language as its own chunk
 * (`<lib>-locale-{es,fil}.<hash>.js`).
 *
 * On boot:
 *   - register() is called by every per-lib `translate.js` (eagerly side-imported
 *     from `apps/ui/src/i18n-bootstrap.js`).
 *   - register() fires `applyEntry(entry, currentLocale)` which kicks the dynamic
 *     import for the persisted locale and adds its promise to `inflight`.
 *   - `apps/ui-shell/src/main.jsx` awaits `polyglotRegistry.ready()` (drains `inflight`)
 *     before importing the rest of the app. That guarantees `t(...)` calls at
 *     module load (e.g. `main-tabs.js`, `main-routes.js`) resolve to the right
 *     language instead of freezing English values.
 *
 * On locale switch the LanguageSwitcher writes localStorage + reloads. The new
 * page goes through the boot path again with the new persisted locale.
 *
 * Failure modes: if a loader throws (network error, 404), `phrasesForEntry`
 * catches it and falls back to the English baseline for that one lib. App stays
 * functional, that lib renders English while others render in chosen locale.
 */
import { mergeDeepRight } from 'ramda';

const SUPPORTED_LOCALES = ['en', 'es', 'fil', 'ja', 'th'];
const DEFAULT_LOCALE = 'en';
const LOCALE_STORAGE_KEY = 'argos.locale';

// Single source of truth for "what locale should we start in".
// 1. Persisted localStorage value (set by the LanguageSwitcher).
// 2. Browser language if it matches a supported locale (first-visit fallback).
// 3. DEFAULT_LOCALE ('en').
//
// Jotai's atomWithStorage round-trips through JSON, so the stored value is
// typically `"es"` (with quotes). Try JSON.parse first; fall back to the raw
// value so entries written by older code paths still resolve.
//
// Used by:
//   - polyglot-registry boot (`currentLocale` initial value, here in this file)
//   - locale.atom.js (`atomWithStorage` initial value)
//   - any other code that needs the boot-time locale without a React hook
const readInitialLocale = () => {
  try {
    if (typeof window === 'undefined') return DEFAULT_LOCALE;
    const raw = window.localStorage?.getItem(LOCALE_STORAGE_KEY);
    let parsed = raw;
    try {
      parsed = JSON.parse(raw);
    } catch (_) {
      /* not JSON — use raw */
    }
    if (parsed && SUPPORTED_LOCALES.includes(parsed)) return parsed;
    /*
    const browser = (window.navigator?.language || '').toLowerCase();
    const match = SUPPORTED_LOCALES.find(loc => browser.startsWith(loc));
    return match || DEFAULT_LOCALE;
    */
    return DEFAULT_LOCALE;
  } catch (_) {
    return DEFAULT_LOCALE;
  }
};

export { LOCALE_STORAGE_KEY, readInitialLocale };

// entries: Array<{ instance, en, loaders: { [locale]: () => Promise<{default}> }, cache: { [locale]: phrases } }>
const entries = [];
const inflight = new Set();
const currentLocale = readInitialLocale();

const phrasesForEntry = async (entry, locale) => {
  if (locale === DEFAULT_LOCALE) return entry.en;
  if (entry.cache[locale]) return entry.cache[locale];
  const loader = entry.loaders?.[locale];
  if (!loader) return entry.en;
  try {
    const mod = await loader();
    const overrides = mod?.default ?? mod;
    const merged = mergeDeepRight(entry.en, overrides);
    // eslint-disable-next-line no-param-reassign
    entry.cache[locale] = merged;
    return merged;
  } catch (_) {
    return entry.en;
  }
};

const applyEntry = (entry, locale) => {
  const promise = phrasesForEntry(entry, locale).then(phrases => {
    entry.instance.replace(phrases);
  });
  inflight.add(promise);
  promise.finally(() => inflight.delete(promise));
  return promise;
};

export const polyglotRegistry = {
  // New form: register(instance, { en, loaders, libName })
  // Legacy form (back-compat for callers not yet migrated): register(instance, bundles)
  register(instance, options) {
    let entry;
    const libName = options?.libName || 'unknown';
    if (options && options.en && options.loaders) {
      entry = { instance, en: options.en, loaders: options.loaders, cache: {}, libName };
    } else {
      // Legacy: options is a static { en, es, fil } bundle map. Wrap as eager loaders.
      const bundles = options || {};
      entry = {
        instance,
        en: bundles.en || {},
        loaders: SUPPORTED_LOCALES.filter(l => l !== DEFAULT_LOCALE).reduce((acc, locale) => {
          if (bundles[locale]) acc[locale] = () => Promise.resolve({ default: bundles[locale] });
          return acc;
        }, {}),
        cache: SUPPORTED_LOCALES.reduce((acc, locale) => {
          if (locale !== DEFAULT_LOCALE && bundles[locale]) acc[locale] = bundles[locale];
          return acc;
        }, {})
      };
    }
    entries.push(entry);
    // Fire-and-forget: load current locale for this instance.
    applyEntry(entry, currentLocale);
  },
  getLocale() {
    return currentLocale;
  },
  // Resolves once all inflight loaders settle. Useful at app boot to delay first render
  // until the persisted-locale chunks have applied to every registered Polyglot.
  ready() {
    const drain = () => {
      if (inflight.size === 0) return Promise.resolve();
      return Promise.allSettled(Array.from(inflight)).then(drain);
    };
    return drain();
  }
};

export { SUPPORTED_LOCALES, DEFAULT_LOCALE };
